const ROLE_LABELS = Object.freeze({
  mentor: 'Mentor',
  patient: 'Patient',
  participant: 'Participant',
  screen: 'Screen',
  'chamber-room': 'Chamber Room',
  panelist: 'Panelist',
  auditor: 'Auditor',
  observer: 'Observer',
  doctor: 'Doctor',
  user: 'User',
})

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeTransportUid(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number(value)

  if (Number.isFinite(numericValue)) {
    return numericValue
  }

  return String(value)
}

function buildTransportUidKeys(uid) {
  const normalizedUid = normalizeTransportUid(uid)

  if (normalizedUid === null) {
    return []
  }

  const keys = [normalizedUid, String(normalizedUid)]
  const numericUid = Number(normalizedUid)

  if (Number.isFinite(numericUid)) {
    keys.push(numericUid)
  }

  return Array.from(new Set(keys))
}

function parseTransportMetadata(rawUsername) {
  const normalizedRawUsername = normalizeOptionalString(rawUsername, '')

  if (!normalizedRawUsername) {
    return {
      rawUsername: '',
      profilePicId: null,
      identityKey: null,
    }
  }

  try {
    const parsedUsername = JSON.parse(normalizedRawUsername)

    if (parsedUsername && typeof parsedUsername === 'object' && !Array.isArray(parsedUsername)) {
      return {
        rawUsername: normalizedRawUsername,
        profilePicId: normalizeOptionalString(parsedUsername?.profilePicId),
        identityKey: normalizeOptionalString(parsedUsername?.identityKey),
      }
    }
  } catch (error) {
    return {
      rawUsername: normalizedRawUsername,
      profilePicId: null,
      identityKey: null,
    }
  }

  return {
    rawUsername: normalizedRawUsername,
    profilePicId: null,
    identityKey: null,
  }
}

export function getParticipantTransportUid(participant) {
  return normalizeTransportUid(
    participant?.transport?.uid ?? participant?.transport?.rtcUid ?? null,
  )
}

function buildUsernameMap(participants = []) {
  return participants.reduce((usernameMap, participant) => {
    const rawUsername = normalizeOptionalString(participant?.transport?.rawUsername, '')
    const transportUid = getParticipantTransportUid(participant)

    if (!rawUsername || transportUid === null) {
      return usernameMap
    }

    buildTransportUidKeys(transportUid).forEach((uidKey) => {
      usernameMap[uidKey] = rawUsername
    })

    return usernameMap
  }, {})
}

function buildRoleLabel(role) {
  return ROLE_LABELS[role] || 'Participant'
}

function buildFocusMenuParticipants(visibleParticipants = []) {
  return visibleParticipants
    .map((participant) => {
      const transportUid = getParticipantTransportUid(participant)

      if (transportUid === null) {
        return null
      }

      const transportMetadata = parseTransportMetadata(participant?.transport?.rawUsername)

      return {
        participantId: participant?.id || participant?.participantId || null,
        uid: transportUid,
        focusUid: transportUid,
        isLocal: participant?.isLocal === true,
        role: participant?.role || 'participant',
        roleLabel: buildRoleLabel(participant?.role),
        name: participant?.displayName || `Participant ${transportUid}`,
        displayName: participant?.displayName || `Participant ${transportUid}`,
        profilePicId: transportMetadata.profilePicId,
        identityKey: transportMetadata.identityKey,
      }
    })
    .filter(Boolean)
}

function buildDebugRemoteUsers(participants = []) {
  return participants
    .filter((participant) => participant?.isLocal !== true)
    .map((participant) => ({
      uid: getParticipantTransportUid(participant),
      participantId: participant?.id || participant?.participantId || null,
      displayName: participant?.displayName || '',
      role: participant?.role || 'participant',
      rawUsername: participant?.transport?.rawUsername || '',
      hasAudio: participant?.audioMuted !== true,
      hasVideo: participant?.videoMuted !== true,
    }))
    .filter((participant) => participant.uid !== null)
}

export function buildChamberShellSnapshot({ controllerState = {} } = {}) {
  const participants = Array.isArray(controllerState?.participants)
    ? controllerState.participants
    : []
  const visibleParticipants = Array.isArray(controllerState?.visibleParticipants)
    ? controllerState.visibleParticipants
    : []
  const dominantParticipantId =
    normalizeOptionalString(controllerState?.dominantParticipantId) ||
    visibleParticipants[0]?.id ||
    null
  const dominantParticipant =
    participants.find((participant) => participant?.id === dominantParticipantId) ||
    visibleParticipants.find((participant) => participant?.id === dominantParticipantId) ||
    null

  return {
    participants,
    visibleParticipants,
    dominantParticipantId,
    activeSpotlightUid: getParticipantTransportUid(dominantParticipant),
    usernameMap: buildUsernameMap(participants),
    remoteUsers: buildDebugRemoteUsers(participants),
    remoteParticipantCount: visibleParticipants.filter((participant) => !participant?.isLocal)
      .length,
    focusMenuParticipants: buildFocusMenuParticipants(visibleParticipants),
  }
}
